import LoadingButton from "@mui/lab/LoadingButton";
import { useFormik } from "formik";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL, handleSendMessage } from "../../utilities";
import { useAuth } from "../../context/auth";
import { useNotifications } from "@toolpad/core/useNotifications";
import React, { useEffect, useState } from "react";
import { StatusGlobal } from "src/interface/enum/StatusGlobal";
import LogForm from "./form/LogForm";
import { ITask } from "src/interface/ITask";
import { IFollowupSchedule } from "src/interface/IFollowupSchedule";
import { getRoutes } from "src/utilities/routes";

const initialValues = {
  priority: undefined,
  dueDate: "",
  createdOn: new Date(),
  dueOn: "",
  startedOn: "",
  endedOn: "",
  desc: "",
  statusTask: StatusGlobal.NotStarted, // Set the type accordingly
  statusDesc: "",
  userRef: "",
  customerRef: "",
  titleTask: "",
  creatorRef: undefined,
  title: "",

  followUpType: undefined,
  scheduleStatus: StatusGlobal.NotStarted,
  period: undefined,
  parentRef: undefined,
  agreementRef: undefined,
  isActive: true,
  isGeneral: "true",
  isSMS: false,
};

const ChangeStatus = ({
  open,
  handleClose,
  Taskdata,
  FollowupData,
  refetch,
}: {
  open: boolean;
  handleClose: () => void;
  Taskdata?: ITask;
  FollowupData?: IFollowupSchedule;
  refetch: () => void;
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const controller = new AbortController();
  const { mutateAsync: updateAsync, isPending } = useMutation({
    mutationFn: async (body: any) => {
      const response = await fetch(API_URL + body.apiName + "/" + body.id, {
        method: "Put",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      console.log("🚀 ~ mutationFn: ~ response:", response);
      return await response.json();
    },
    onSuccess: (responseData) => {
      JSON.stringify(responseData);
    },
    onError: (error) => {
      console.log(error);
    },
  });
  const Routes = getRoutes();
  const { mutateAsync: archiveAndCreateLogAsync, isPending: isArchivePending } =
    useMutation({
      mutationFn: async (body: any) => {
        const response = await fetch(Routes.TaskLogRoutes + body.oldId, {
          method: "PUT",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
          body: JSON.stringify({
            statusTask: body.statusTask,
            statusDesc: body.statusDesc ?? "",
            userRef: body.userRef,
            taskRef: body.taskRef ?? null,
            followupRef: body.followupRef ?? null,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          return { ...data, _httpStatus: response.status, _ok: false };
        }
        return { ...data, _httpStatus: response.status, _ok: true };
      },
      onSuccess: (responseData) => {
        JSON.stringify(responseData);
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const { mutateAsync: insertLogAsync, isPending: isLogPending } = useMutation({
    mutationFn: async (body: any) => {
      const response = await fetch(Routes.TaskLogRoutes, {
        method: "Post",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json-patch+json",
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify(body),
      });
      return await response.json();
    },
    onSuccess: (responseData) => {
      JSON.stringify(responseData);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const notifications = useNotifications();
  const [logUrl, setLogUrl] = useState<string>("");
  useEffect(() => {
    const baseUrl = "task-logs/history/";
    if (Taskdata) {
      setLogUrl(baseUrl + "by-task/" + Taskdata.id);
    } else if (FollowupData) {
      setLogUrl(baseUrl + "by-followup/" + FollowupData.id);
    } else {
      setLogUrl(baseUrl + "by-followup/0");
    }
  }, [Taskdata, FollowupData]);

  const {
    data: LogsData,
    refetch: refetchLogs,
    isLoading,
  } = useQuery({
    queryKey: ["LogsData" + logUrl],
    queryFn: () =>
      fetch(API_URL + logUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Accept: "application/json",
        },
      }).then((res) => res.json()),
    enabled:
      !!user?.token && !!logUrl && logUrl !== "task-logs/history/by-followup/0", // فقط زمانی که token و logUrl معتبر است
  });

  const formik = useFormik({
    initialValues, // مقادیر اولیه فرم

    onSubmit: async (values) => {
      // بررسی تغییرات: وضعیت، کارمند، یا توضیحات (از != null استفاده می‌شود تا value=0 معتبر باشد)
      const hasStatusChange = Taskdata
        ? values.statusTask != null && values.statusTask !== Taskdata.statusTask
        : values.scheduleStatus != null &&
          values.scheduleStatus !== FollowupData?.lastTaskLog?.statusTask;

      // بررسی تغییر کارمند (فقط برای Task)
      // اگر userRef وجود دارد و با مقدار قبلی متفاوت است
      const hasUserChange = Taskdata
        ? values.userRef !== undefined &&
          values.userRef !== null &&
          values.userRef !== "" &&
          values.userRef !== Taskdata.userRef
        : false; // برای Followup کارمند در لاگ ذخیره می‌شود اما تغییر Task نیست

      const hasDescription = values.desc && values.desc.trim().length > 0;

      // بررسی اینکه آیا حداقل یکی از سه مورد تغییر کرده باشد
      if (!hasStatusChange && !hasUserChange && !hasDescription) {
        handleSendMessage(
          notifications,
          "لطفاً حداقل یکی از موارد زیر را تغییر دهید: وضعیت، کارمند، یا توضیحات",
          "warning"
        );
        return;
      }

      // اگر وضعیت تغییر نکرده، از وضعیت قبلی استفاده کن
      const finalStatusTaskRaw = Taskdata
        ? (values.statusTask ?? Taskdata.statusTask)
        : (values.scheduleStatus ??
          FollowupData?.lastTaskLog?.statusTask ??
          StatusGlobal.NotStarted);
      const finalStatusTask =
        typeof finalStatusTaskRaw === "string"
          ? Number(finalStatusTaskRaw)
          : finalStatusTaskRaw;
      let res: any;
      if (Taskdata) {
        // اگر وضعیت یا کارمند تغییر کرده، Task را به‌روزرسانی کن
        if (hasStatusChange || hasUserChange) {
          res = await updateAsync({
            ...values,
            id: Taskdata.id,
            desc: Taskdata.desc,
            statusDesc: values.desc,
            statusTask: finalStatusTask,
            userRef: values.userRef || Taskdata.userRef, // استفاده از کارمند جدید یا قبلی
            dueDate: new Date(values.dueDate),
            createdOn: new Date(values.createdOn),
            apiName: "tasks",
          });
        } else {
          res = { status: true }; // اگر فقط توضیحات تغییر کرده، نیازی به به‌روزرسانی Task نیست
        }

        // همیشه لاگ را ثبت کن (اگر هر کدام از موارد تغییر کرده باشد)
        if (res.status === true || (!hasStatusChange && !hasUserChange)) {
          const resLog = await insertLogAsync({
            statusTask: finalStatusTask,
            statusDesc: values.desc || "",
            userRef: values.userRef || Taskdata.userRef, // استفاده از کارمند جدید یا قبلی
            taskRef: Taskdata?.id ?? undefined,
            followupRef: undefined,
          });
          if (resLog.status === false) {
            handleSendMessage(
              notifications,
              "خطا در ذخیره سازی لاگ" + (resLog?.message ?? ""),
              "error"
            );
            return;
          }
        }
      } else if (FollowupData) {
        // برای FollowupData، userRef از formik می‌آید (که در LogForm تنظیم می‌شود)
        // اگر userRef خالی باشد، از کاربر فعلی استفاده کن
        const finalUserRef = values.userRef || user?.id;

        const hasExistingLog = !!FollowupData?.lastTaskLog?.id;

        let resLog;
        if (hasExistingLog) {
          resLog = await archiveAndCreateLogAsync({
            statusTask: finalStatusTask,
            statusDesc: values.desc || "",
            userRef: finalUserRef,
            taskRef: undefined,
            followupRef: FollowupData?.id ?? undefined,
            oldId: FollowupData?.lastTaskLog?.id ?? 0,
          });
          // اگر لاگ قبلی دیگر وجود نداشت (۴۰۴)، به‌جای آرشیو فقط لاگ جدید بساز
          if (resLog._ok === false && resLog._httpStatus === 404) {
            resLog = await insertLogAsync({
              statusTask: finalStatusTask,
              statusDesc: values.desc || "",
              userRef: finalUserRef,
              taskRef: undefined,
              followupRef: FollowupData?.id ?? undefined,
            });
          }
        } else {
          resLog = await insertLogAsync({
            statusTask: finalStatusTask,
            statusDesc: values.desc || "",
            userRef: finalUserRef,
            taskRef: undefined,
            followupRef: FollowupData?.id ?? undefined,
          });
        }

        if (resLog.status === false || resLog._ok === false) {
          handleSendMessage(
            notifications,
            "خطا در ذخیره سازی لاگ" + (resLog?.message ?? ""),
            "error"
          );
          return;
        }
      }

      // پیام موفقیت بر اساس تغییرات انجام شده
      const changes: string[] = [];
      if (hasStatusChange) changes.push("وضعیت");
      if (hasUserChange) changes.push("کارمند");
      if (hasDescription) changes.push("توضیحات");

      const successMessage =
        changes.length > 0
          ? `تغییر ${changes.join("، ")} با موفقیت انجام شد!`
          : "ذخیره‌سازی انجام شد!";
      handleSendMessage(notifications, successMessage, "success");
      refetchLogs && refetchLogs();
      // باطل کردن کش لیست پیگیری‌های داشبورد تا وضعیت جدید فوراً نمایش داده شود
      if (FollowupData) {
        await queryClient.invalidateQueries({
          queryKey: ["FollowupsScheduleDataDashboard"],
        });
      }
      refetch && (await refetch());
      handleClose();

      formik.resetForm();
    },
  });

  useEffect(() => {
    if (Taskdata) {
      formik.setValues(Taskdata as any);
      Taskdata.dueDate &&
        formik.setFieldValue("dueDate", new Date(Taskdata.dueDate));
      Taskdata.createdOn &&
        formik.setFieldValue("createdOn", new Date(Taskdata.createdOn));
    } else if (FollowupData) {
      formik.setValues(FollowupData as any);
      FollowupData.dueOn &&
        formik.setFieldValue("dueOn", new Date(FollowupData.dueOn));
      FollowupData.dueOn &&
        formik.setFieldValue("dueOn", new Date(FollowupData.dueOn));
      FollowupData.startedOn &&
        formik.setFieldValue("startedOn", new Date(FollowupData.startedOn));
      FollowupData.endedOn &&
        formik.setFieldValue("endedOn", new Date(FollowupData.endedOn));
    }

    formik.setFieldValue("desc", "");
    // وضعیت را خالی نکن، بلکه مقدار قبلی را نگه دار
    if (Taskdata) {
      formik.setFieldValue("statusTask", Taskdata.statusTask);
    } else if (FollowupData) {
      formik.setFieldValue(
        "scheduleStatus",
        FollowupData?.lastTaskLog?.statusTask || StatusGlobal.NotStarted
      );
    }
  }, [Taskdata, FollowupData]);

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        PaperProps={{
          component: "form",
          onSubmit: formik.handleSubmit,
          // sx: {
          //   width: "100%",
          // },
        }}
      >
        <DialogTitle>
          تغییر وضعیت "{Taskdata ? Taskdata?.titleTask : FollowupData?.title}"
        </DialogTitle>
        <DialogContent>
          <LogForm
            formik={formik}
            taskData={Taskdata}
            schedulData={FollowupData}
            LogsData={LogsData?.status === false ? [] : LogsData?.data}
            isLoading={isLoading}
          />
        </DialogContent>
        <DialogActions>
          <LoadingButton
            loading={isPending || isLogPending || isArchivePending}
            variant="contained"
            color="success"
            size="medium"
            fullWidth
            type="submit"
          >
            ذخیره
          </LoadingButton>
          <Button color="error" variant="contained" onClick={handleClose}>
            انصراف
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default ChangeStatus;
